import React, { useRef } from 'react';
import { SaleContactSection, SaleHeaderSection, BreadCrumbSection, SaleListItem } from './types';
import { LIST_SECTION_LABELS } from './constants';
import { LabelRow, SplitRow, DraftTextField, DraftTextArea } from './primitives';

interface SaleEditorProps {
  data:     SaleContactSection;
  onChange: (v: SaleContactSection) => void;
}

const SaleEditor: React.FC<SaleEditorProps> = ({ data, onChange }) => {
  const header    = data.content[0]?.headerSection;
  const orig      = useRef(data);
  const origCrumb = orig.current.content[0]?.breadCrumbSection;

  const setHeader = (p: Partial<SaleHeaderSection>) =>
    onChange({
      ...data,
      content: [{ ...data.content[0], headerSection: { ...header, ...p } }],
    });

  const setCrumb = (p: Partial<BreadCrumbSection>) =>
    onChange({
      ...data,
      content: [{
        ...data.content[0],
        breadCrumbSection: { ...data.content[0].breadCrumbSection, ...p },
      }],
    });

  const setListField = (idx: number, field: keyof SaleListItem, v: string) => {
    const updatedLists = header.lists.map((item, i) =>
      i === idx ? { ...item, [field]: v } : item
    );
    setHeader({ lists: updatedLists });
  };

  if (!header) return null;

  return (
    <>
      {/* bold=true because this LabelRow has a toggle */}
      <LabelRow
        label="Header section"
        bold
        toggle={{ checked: header.visible, onChange: v => setHeader({ visible: v }) }}
      />

      <LabelRow label="Main title" />
      <SplitRow currentText={orig.current.content[0]?.headerSection?.title ?? ''}>
        <DraftTextField placeholder="Text title" onChange={v => setHeader({ title: v })} />
      </SplitRow>

      <LabelRow label="Title description" />
      <SplitRow currentText={orig.current.content[0]?.headerSection?.description ?? ''} multiline>
        <DraftTextArea placeholder="Title description" rows={3}
          onChange={v => setHeader({ description: v })} />
      </SplitRow>

      {header.lists.map((item, idx) => {
        const sectionLbl = LIST_SECTION_LABELS[idx] ?? `Section ${idx + 1}`;
        const origItem   = orig.current.content[0]?.headerSection?.lists[idx];
        return (
          <React.Fragment key={item.id}>
            <LabelRow label={sectionLbl} />
            <SplitRow currentText={origItem?.title ?? ''}>
              <DraftTextField
                key={`title-${idx}`}
                placeholder="Text title"
                onChange={v => setListField(idx, 'title', v)} />
            </SplitRow>

            <LabelRow label="Secondary button" />
            <SplitRow currentText={origItem?.label ?? ''}>
              <DraftTextField
                key={`label-${idx}`}
                placeholder="Text button"
                onChange={v => setListField(idx, 'label', v)} />
            </SplitRow>
          </React.Fragment>
        );
      })}

      {origCrumb && (
        <>
          {/* bold=true because this LabelRow has a toggle */}
          <LabelRow
            label="Breadcrumb section"
            bold
            toggle={{
              checked:  data.content[0]?.breadCrumbSection?.visible ?? true,
              onChange: v => setCrumb({ visible: v }),
            }}
          />

          <LabelRow label="Breadcrumb — first title" />
          <SplitRow currentText={origCrumb.breadCrumbTitleSt}>
            <DraftTextField placeholder="e.g. Contact us"
              onChange={v => setCrumb({ breadCrumbTitleSt: v })} />
          </SplitRow>

          <LabelRow label="Breadcrumb — second title" />
          <SplitRow currentText={origCrumb.breadCrumbTitleNd}>
            <DraftTextField placeholder="e.g. Sales"
              onChange={v => setCrumb({ breadCrumbTitleNd: v })} />
          </SplitRow>
        </>
      )}
    </>
  );
};

export default SaleEditor;